import React, { useCallback, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  LaunchOutlined,
  LinkOutlined,
} from '@mui/icons-material';
import {
  ActionButtonsGroup,
  ActionButtonProps,
  ConfirmDialogControlled,
  CloseDetailsDrawer,
} from 'components';
import { appLinks } from 'routes/links';
import { InfluencerTypes, users, UserTypes } from 'api';
import { useApiRequest, useClipboard, useFiltersSearchParams } from 'hooks';

interface Props {
  inline?: boolean;
  user: UserTypes.ItemData;
}

const Actions = (props: Props): JSX.Element => {
  const { user, inline } = props;
  const navigate = useNavigate();
  const handleCopy = useClipboard();
  const { handleFilterChange } = useFiltersSearchParams();

  const { isLoading, requestFn } = useApiRequest(() =>
    users.remove(user._id.$oid),
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleOpenDialog = () => setIsDeleteDialogOpen(true);
  const handleCloseDialog = () => setIsDeleteDialogOpen(false);

  const handleDelete = useCallback(async () => {
    await requestFn({
      successMessage: 'User deleted',
    });

    setIsDeleteDialogOpen(false);

    if (inline) {
      handleFilterChange('splitItemId', undefined);
    } else {
      navigate(-1);
    }
  }, [handleFilterChange, inline, navigate, requestFn]);

  const actions: ActionButtonProps<InfluencerTypes.ItemData>[] = useMemo(
    () => [
      {
        name: `Open fullscreen`,
        Icon: LaunchOutlined,
        to: (item) => appLinks.team.details.as(item._id.$oid),
        hidden: !inline,
      },
      {
        name: `Copy link`,
        Icon: LinkOutlined,
        onClick: (item) =>
          handleCopy(
            window.location.origin + appLinks.team.details.as(item._id.$oid),
          ),
      },
      {
        name: 'Edit user',
        EditButtonProps: {
          inline,
          idKey: 'userId',
          id: (item) => item._id?.$oid,
          link: appLinks.team.edit.as,
        },
      },
      {
        name: 'Delete user',
        Icon: DeleteOutlined,
        onClick: handleOpenDialog,
      },
    ],
    [handleCopy, inline],
  );

  return (
    <Grid container spacing={2} alignItems="center" wrap="nowrap">
      {inline && (
        <Grid item flexGrow={1}>
          <CloseDetailsDrawer />
        </Grid>
      )}
      <Grid item>
        <ActionButtonsGroup
          maxActions={inline ? 7 : 4}
          actions={actions}
          data={user}
        />
        <ConfirmDialogControlled
          isLoading={isLoading}
          name="user"
          isOpen={isDeleteDialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleDelete}
        />
      </Grid>
    </Grid>
  );
};

export default Actions;
